"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { STATS } from "@/lib/constants";

/* Flagship mock for "Real Residential IPs" — the exit pool as it appears in the
   dashboard: a live table of exits with country, city and ISP, over a header
   that carries the pool's headline numbers.

   A TABLE on purpose, not a map: the residential page already carries an
   interactive globe two sections down, and a second map here would say the same
   thing twice. The table says something the globe can't — that every exit is a
   named residential ISP in a real city.

   Token-based so it reads in both themes, fixed row count so the card height
   never moves. Decorative. */

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 12%, transparent)";
const FIELD_BG = "color-mix(in srgb, var(--foreground) 3%, transparent)";

/* One template for the head and the rows, so a column can't drift out of
   alignment between them.

   `minmax(0, …)` on every flexible track, not a bare `1fr`: a grid track's
   default minimum is auto, which refuses to shrink below its content, so the
   `truncate` on these cells would never engage and the table would push past
   the card on a narrow screen instead of ellipsing. The ISP column is dropped
   below `sm` — at ~310px of card there isn't room for four columns of real
   values, and truncating two of them reads worse than showing three whole. */
const TABLE_GRID =
  "grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto]";

/* The row that swaps carries `rotates` — it re-renders on the pool tick to show
   a fresh exit taking over, which is the one thing a static table can't say. */
const POOL_ROWS = [
  { id: "p1", flag: "🇺🇸", ip: "98.112.4.21", city: "Austin, TX", isp: "Spectrum" },
  { id: "p2", flag: "🇩🇪", ip: "84.171.9.30", city: "Hamburg", isp: "Vodafone" },
  { id: "p3", flag: "🇯🇵", ip: "126.34.88.7", city: "Osaka", isp: "NTT Docomo" },
  { id: "p4", flag: "🇧🇷", ip: "177.42.19.8", city: "São Paulo", isp: "Claro" },
];

/** The exit that cycles in the last slot, so the table reads as live. */
const ROTATING_ROWS = [
  { id: "r1", flag: "🇬🇧", ip: "81.99.140.6", city: "Manchester", isp: "BT" },
  { id: "r2", flag: "🇨🇦", ip: "99.240.11.52", city: "Toronto", isp: "Rogers" },
  { id: "r3", flag: "🇦🇺", ip: "1.129.108.4", city: "Melbourne", isp: "Telstra" },
];

export default function IpPoolMock() {
  const reduced = useReducedMotion();

  return (
    <div
      className="border-border bg-popover w-full overflow-hidden rounded-xl border"
      aria-hidden="true"
    >
      {/* A panel header, deliberately NOT two big numbers: STATS.ips is the
          phrase "Millions of", not a count — the brand does not claim a hard IP
          figure — and a fabricated one here would be the only invented number
          on the page. So the scale is stated in words and the table carries the
          detail. */}
      <div className="border-border flex items-start justify-between border-b px-4 py-3">
        <div>
          <div className="text-foreground text-[11px] font-semibold tracking-wide uppercase">
            Exit pool
          </div>
          <div className="text-muted-foreground mt-1 text-[10px]">
            {STATS.ips} residential IPs · {STATS.countries} countries
          </div>
        </div>
        <span
          className="flex items-center gap-1.5 text-[10px] font-medium"
          style={{ color: "var(--brand-green)" }}
        >
          <span className="relative flex h-1.5 w-1.5">
            {!reduced && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                style={{ backgroundColor: "var(--brand-green)" }}
              />
            )}
            <span
              className="relative inline-flex h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
          </span>
          Live
        </span>
      </div>

      {/* Column heads, so the rows read as a real table rather than loose chips */}
      <div
        className={cn(
          TABLE_GRID,
          "text-muted-foreground gap-2 px-4 pt-2.5 pb-1.5 text-[9px] tracking-wide uppercase"
        )}
      >
        <span>Exit IP</span>
        <span>City</span>
        <span className="hidden sm:block">ISP</span>
        <span className="text-right">Type</span>
      </div>

      <div className="divide-border divide-y">
        {POOL_ROWS.map((row) => (
          <PoolRow key={row.id} {...row} />
        ))}
        {/* The live slot — same row shape, but its contents cycle */}
        <RotatingPoolRow reduced={reduced} />
      </div>

      <div
        className="border-border flex items-center justify-between border-t px-4 py-2.5"
        style={{ backgroundColor: FIELD_BG }}
      >
        {/* The card's own description already says "ethically sourced from
            opt-in devices", so the footer says what the TABLE is instead. */}
        <span className="text-muted-foreground text-[10px]">Sample of the live pool</span>
        <span
          className="rounded-md px-2 py-0.5 text-[9px] font-semibold"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          KYC required
        </span>
      </div>
    </div>
  );
}

interface PoolRowProps {
  flag: string;
  ip: string;
  city: string;
  isp: string;
}

function PoolRow({ flag, ip, city, isp }: PoolRowProps) {
  return (
    <div className={cn(TABLE_GRID, "items-center gap-2 px-4 py-2")}>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="text-[11px] leading-none">{flag}</span>
        <span className="text-foreground truncate font-mono text-[11px]">{ip}</span>
      </span>
      <span className="text-muted-foreground truncate text-[10px]">{city}</span>
      <span className="text-muted-foreground hidden truncate text-[10px] sm:block">{isp}</span>
      <span className="text-right text-[9px] font-medium" style={{ color: "var(--brand-green)" }}>
        Residential
      </span>
    </div>
  );
}

interface RotatingPoolRowProps {
  reduced: boolean | null;
}

/* Cycles through ROTATING_ROWS on a 3s beat. Under reduced motion it settles on
   the first entry and never moves — a static row, not a blank one. */
function RotatingPoolRow({ reduced }: RotatingPoolRowProps) {
  if (reduced) {
    return <PoolRow {...ROTATING_ROWS[0]} />;
  }

  /* All three are mounted and cross-faded on offset keyframes rather than
     swapped by a timer: no interval to clean up, no state, and the loop pauses
     with the rest of the page's animations. The row keeps a fixed height so the
     table never reflows as they trade places. */
  return (
    <div className="relative h-8.25">
      {ROTATING_ROWS.map((row, i) => (
        <motion.div
          key={row.id}
          className="absolute inset-0"
          initial={{ opacity: i === 0 ? 1 : 0 }}
          animate={{ opacity: [0, 1, 1, 0, 0] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.06, 0.28, 0.34, 1],
            delay: i * 3,
          }}
        >
          <PoolRow {...row} />
        </motion.div>
      ))}
    </div>
  );
}
